import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RbacAdminService {

  private baseUrl = environment.BASE_LA + '/api/admin/rbac';

  constructor(private http: HttpClient) { }

  getRoles(): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles`).toPromise();
  }

  getUsers(params: { page?: number; limit?: number; search?: string; status?: string; role?: string; department?: string; level?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    if (params.role) queryParams.set('role', params.role);
    if (params.department) queryParams.set('department', params.department);
    if (params.level) queryParams.set('level', params.level);
    return this.http.get(`${this.baseUrl}/users?${queryParams.toString()}`).toPromise();
  }

  assignRole(userId: string, roleCode: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/assign-role`, { userId, roleCode }).toPromise();
  }

  removeRole(userId: string, roleCode?: string): Promise<any> {
    const body: any = { userId };
    if (roleCode) body.roleCode = roleCode;
    return this.http.post(`${this.baseUrl}/remove-role`, body).toPromise();
  }

  getRolePermissions(roleId: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/role/${roleId}/permissions`).toPromise();
  }

  getUserPermissions(userId: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/users/${userId}/permissions`).toPromise();
  }

  createRole(roleData: {
    code: string;
    name: string;
    description: string;
    department: string;
    level: number;
    permissions?: { module: string; submodule: string; actions: string[] }[];
  }): Promise<any> {
    return this.http.post(`${this.baseUrl}/roles`, roleData).toPromise();
  }

  deleteRole(roleCode: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/roles/${roleCode}`).toPromise();
  }

  getRolePermissionsByCode(roleCode: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles/${roleCode}/permissions-matrix`).toPromise();
  }

  updateRolePermissions(roleCode: string, permissions: { module: string; submodule: string; actions: string[] }[]): Promise<any> {
    return this.http.put(`${this.baseUrl}/roles/${roleCode}/permissions`, { permissions }).toPromise();
  }

  getAllDepartments(): Promise<any> {
    return this.http.get(`${this.baseUrl}/departments/all`).toPromise();
  }

  createDepartment(name: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/departments`, { name }).toPromise();
  }

  deleteDepartment(name: string): Promise<any> {
    return this.http.delete(`${this.baseUrl}/departments/${encodeURIComponent(name)}`).toPromise();
  }

  reassignDepartment(sourceDepartment: string, targetDepartment: string): Promise<any> {
    return this.http.post(`${this.baseUrl}/departments/reassign`, { sourceDepartment, targetDepartment }).toPromise();
  }

  getRoleUsers(roleCode: string): Promise<any> {
    return this.http.get(`${this.baseUrl}/roles/${roleCode}/users`).toPromise();
  }

  updateRoleDepartment(roleCode: string, department: string | null): Promise<any> {
    return this.http.put(`${this.baseUrl}/roles/${roleCode}/department`, { department }).toPromise();
  }

  getAuditLogs(params: { page?: number; limit?: number; action?: string; entity?: string; search?: string; dateFrom?: string; dateTo?: string } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.action && params.action !== 'all') queryParams.set('action', params.action);
    if (params.entity && params.entity !== 'all') queryParams.set('entity', params.entity);
    if (params.search) queryParams.set('search', params.search);
    if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.set('dateTo', params.dateTo);
    return this.http.get(`${this.baseUrl}/audit-logs?${queryParams.toString()}`).toPromise();
  }
}
